import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { FaTrash, FaEdit, FaTruckLoading, FaPhone, FaEnvelope } from "react-icons/fa";
import api from "../../api";

const EMPTY_FORM = { name: "", contact_name: "", email: "", phone: "", notes: "", active: true };

const SubcontractorsPage = () => {
  const { t } = useTranslation();
  const [subcontractors, setSubcontractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchSubcontractors = async () => {
    try {
      const res = await api.get("/subcontractors");
      setSubcontractors(res.data);
    } catch {
      toast.error(t("subcontractors.fetchError", "Échec du chargement des sous-traitants."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSubcontractors(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => { setForm(EMPTY_FORM); setEditingId(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      if (editingId) {
        await api.put(`/subcontractors/${editingId}`, form);
        toast.success(t("subcontractors.updated", "Sous-traitant mis à jour."));
      } else {
        await api.post("/subcontractors", form);
        toast.success(t("subcontractors.created", "Sous-traitant ajouté."));
      }
      resetForm();
      fetchSubcontractors();
    } catch (err) {
      toast.error(err?.response?.data?.error || t("subcontractors.saveError", "Erreur lors de l'enregistrement."));
    }
  };

  const handleEdit = (sc) => {
    setForm({
      name: sc.name || "", contact_name: sc.contact_name || "", email: sc.email || "",
      phone: sc.phone || "", notes: sc.notes || "", active: !!sc.active,
    });
    setEditingId(sc.id);
  };

  const handleDelete = (id) => setConfirmDeleteId(id);

  const confirmDelete = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await api.delete(`/subcontractors/${id}`);
      toast.success(t("subcontractors.deleted", "Sous-traitant supprimé."));
      if (editingId === id) resetForm();
      fetchSubcontractors();
    } catch {
      toast.error(t("subcontractors.deleteError", "Erreur lors de la suppression."));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <FaTruckLoading className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-base-content">{t("subcontractors.title", "Sous-traitants")}</h1>
          <p className="text-sm text-base-content/40 mt-0.5">{t("subcontractors.subtitle", "Partenaires externes pour les prestations sous-traitées.")}</p>
        </div>
      </div>

      {/* Create / Edit form */}
      <div className="bg-base-100 border border-base-200 rounded-xl shadow-sm p-6 mb-8">
        <h2 className="text-base font-semibold text-base-content mb-4">
          {editingId ? t("subcontractors.editOne", "Modifier le sous-traitant") : t("subcontractors.createNew", "Ajouter un sous-traitant")}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-base-content/70 mb-1">{t("subcontractors.name", "Nom / société")} *</label>
              <input
                type="text" required
                className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none w-full"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-base-content/70 mb-1">{t("subcontractors.contactName", "Contact")}</label>
              <input
                type="text"
                className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none w-full"
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-base-content/70 mb-1">{t("subcontractors.email", "Email")}</label>
              <input
                type="email"
                placeholder={t("subcontractors.emailHint", "Requis pour l'envoi de la lettre de voiture")}
                className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none w-full"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-base-content/70 mb-1">{t("subcontractors.phone", "Téléphone")}</label>
              <input
                type="tel"
                className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none w-full"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1">{t("subcontractors.notes", "Notes")}</label>
            <textarea
              rows={2}
              className="textarea textarea-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none w-full"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer w-fit">
            <input
              type="checkbox"
              className="checkbox checkbox-primary checkbox-sm"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            <span className="text-sm text-base-content/70">{t("subcontractors.active", "Actif")}</span>
          </label>

          <div className="flex justify-end pt-2">
            {editingId && (
              <button type="button" onClick={resetForm} className="btn btn-ghost mr-2">
                {t("buttons.cancel")}
              </button>
            )}
            <button type="submit" className="btn btn-primary">
              {editingId ? t("buttons.edit") : t("buttons.add")}
            </button>
          </div>
        </form>
      </div>

      {/* Table */}
      <h3 className="text-base font-semibold text-base-content mb-3">{t("subcontractors.list", "Sous-traitants enregistrés")}</h3>
      <div className="overflow-x-auto rounded-xl border border-base-200 bg-base-100 shadow-sm">
        <table className="table table-sm">
          <thead className="bg-base-200 border-b border-base-200">
            <tr>
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase py-3">{t("subcontractors.name", "Nom / société")}</th>
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase py-3">{t("subcontractors.contact", "Contact")}</th>
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase py-3 text-center">{t("subcontractors.status", "Statut")}</th>
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase py-3">{t("mission.list.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-200">
            {subcontractors.map((sc) => (
              <tr key={sc.id} className="hover:bg-base-200/50 transition-colors group">
                <td className="py-3 font-medium text-base-content text-sm">
                  {sc.name}
                  {sc.contact_name && <div className="text-xs text-base-content/40">{sc.contact_name}</div>}
                </td>
                <td className="py-3 text-sm text-base-content/70">
                  {sc.email && <div className="flex items-center gap-1.5"><FaEnvelope size={11} className="text-base-content/30" /> {sc.email}</div>}
                  {sc.phone && <div className="flex items-center gap-1.5 mt-0.5"><FaPhone size={11} className="text-base-content/30" /> {sc.phone}</div>}
                  {!sc.email && !sc.phone && <span className="text-base-content/30">—</span>}
                </td>
                <td className="py-3 text-center">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border ${
                    sc.active
                      ? "bg-success/10 text-success border-success/20"
                      : "bg-base-200 text-base-content/50 border-base-300"
                  }`}>
                    {sc.active ? t("subcontractors.active", "Actif") : t("subcontractors.inactive", "Inactif")}
                  </span>
                </td>
                <td className="py-3">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleEdit(sc)}
                      className="w-7 h-7 rounded-lg text-base-content/40 hover:text-base-content hover:bg-base-300 flex items-center justify-center transition-colors"
                      title={t("buttons.edit")}
                    >
                      <FaEdit className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(sc.id)}
                      className="w-7 h-7 rounded-lg text-base-content/40 hover:text-error hover:bg-error/10 flex items-center justify-center transition-colors"
                      title={t("buttons.delete")}
                    >
                      <FaTrash className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {subcontractors.length === 0 && (
              <tr>
                <td colSpan={4} className="py-16 text-center text-base-content/40 text-sm">
                  {t("subcontractors.empty", "Aucun sous-traitant pour l'instant.")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Confirm delete */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-xl border border-base-200 shadow-2xl p-6 max-w-sm w-full animate-fade-in">
            <h3 className="font-bold text-base-content text-base mb-1">{t("subcontractors.confirmDelete", "Supprimer ce sous-traitant ?")}</h3>
            <p className="text-sm text-base-content/50 mb-5">{t("user.deleteIrreversible", "Cette action est irréversible.")}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDeleteId(null)} className="btn btn-ghost btn-sm">{t("buttons.cancel")}</button>
              <button onClick={confirmDelete} className="btn btn-error btn-sm">{t("buttons.delete")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubcontractorsPage;
